import {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {useLocation} from 'react-router-dom';
import type {Step} from 'intro.js-react';
import {Steps} from '../../../app/components/Intro';
import {useAppDispatch} from '../../../app/hooks/useAppDispatch';
import {useAppSelector} from '../../../app/hooks/useAppSelector';
import {useGetBiobookChaptersQuery, useGetBiobookQuery} from '../../../app/services/biobookApi';
import {useGetBiotwinQuery} from '../../../app/services/biotwinApi';
import {useAppEventListen} from '../../core/hooks/useAppEventListen';
import {useSessionState} from '../../auth/useSessionState';
import {RelevantCondition} from '../../biotwin/types';
import {useAssistantUtils} from '../../core/hooks/useAssistantUtils';
import {useDemo} from '../../demo/hooks/useDemo';
import {PersonContext} from '../../person/contexts/PersonContext';
import {
  selectAllPerioperativeVisibleConditionIds,
  selectSelectedBiobookPageInfo,
  selectSelectedConditionIds,
  selectSelectedFilter,
  setSelectedConditionIds,
  setSelectedFilter,
} from '../../person/personSlice';
import {BiobookTableOfContents, BiobookTableOfContentsHandle} from '../components/BiobookTableOfContents';
import {TableOfContents} from '../helpers/TableOfContents';
import {BiobookChapter as IBiobookChapter, BiobookPage as IBiobookPage} from '../types';
import {isInPerioperative, isPerioperativeSetting, isProcedure} from '../../biotwin/helpers/utils';
import {BiobookFilterPanel} from '../components/BiobookFilterPanel';
import {useBiobookPages} from '../hooks/useBiobookPages';
import {BiobookTableOfContentsSkeleton} from '../components/BiobookTableOfContentsSkeleton';
let lastBiobookPageOpenTimestamp = 0;
export const Biobook = () => {
  const {biotwinConditions, relevantConditions, userId} = useContext(PersonContext);
  const location = useLocation();
  const {data: biotwin} = useGetBiotwinQuery(
    {
      id: userId,
    },
    {
      skip: !userId,
    },
  );
  const [introDisplayed, setIntroDisplayed] = useSessionState('intro.biobook.displayed');
  const [stepsEnabled, setStepsEnabled] = useState(false);
  const dispatch = useAppDispatch();
  const allPerioperativeVisibleConditionIds = useAppSelector(selectAllPerioperativeVisibleConditionIds);
  const selectedBiobookPageInfo = useAppSelector(selectSelectedBiobookPageInfo);
  const selectedConditionIds = useAppSelector(selectSelectedConditionIds);
  const selectedFilter = useAppSelector(selectSelectedFilter);
  const selectedConditions = selectedConditionIds
    .map((conditionId) =>
      relevantConditions?.find((relevantCondition) => relevantCondition.conditionId === conditionId),
    )
    .filter((value): value is RelevantCondition => value != null);
  const inPerioperativeRelevantConditions =
    relevantConditions?.filter((rc) => {
      const biotwinCondition = biotwinConditions.find((bc) => bc.conditionId === rc.conditionId);
      return biotwinCondition != null && isInPerioperative(biotwinCondition);
    }) ?? [];
  const {
    data: biobook = [],
    isFetching: isFetchingGetBiobook,
    isLoading: isLoadingGetBiobook,
  } = useGetBiobookQuery({
    userId,
  });
  const {data: biobookChapters = [], isFetching: isFetchingBiobookChapters} = useGetBiobookChaptersQuery({
    userId,
  });
  const {
    multiIndexedBiobookPages,
    drugMultiIndexedBiobookPages,
    neoplasticBiobookPages,
    standardAndPolicyBiobookPages,
    bookmarkedBiobookPages,
    updatedBiobookPages,
  } = useBiobookPages();
  const filteredBiobookPages = useMemo(
    () => biobookPagesFactory(),
    [
      allPerioperativeVisibleConditionIds,
      biobook,
      bookmarkedBiobookPages,
      biobookChapters,
      updatedBiobookPages,
      biotwinConditions,
      selectedConditionIds,
      selectedFilter,
    ],
  );
  const isComposing = isFetchingBiobookChapters || isFetchingGetBiobook;
  const filteredBiobookChapters = useMemo(
    () => filterBiobookChapters(biobookChapters, filteredBiobookPages),
    [biobookChapters, filteredBiobookPages],
  );
  const noEmptyRoots = selectedConditionIds.length > 0 || selectedFilter != null;
  const tableOfContents = useMemo(
    () => new TableOfContents(filteredBiobookChapters, noEmptyRoots),
    [filteredBiobookChapters, noEmptyRoots],
  );
  const {isDemo} = useDemo();
  const {pushAssistantContent} = useAssistantUtils();
  const biobookTableOfContents = useRef<BiobookTableOfContentsHandle>(null);
  useEffect(() => {
    if (!isComposing) {
      if (selectedBiobookPageInfo != null && lastBiobookPageOpenTimestamp < selectedBiobookPageInfo.timestamp) {
        let frame = requestAnimationFrame(() => {
          frame = requestAnimationFrame(() => {
            setTimeout(() => {
              biobookTableOfContents.current?.showBiobookPage(selectedBiobookPageInfo.id);
              lastBiobookPageOpenTimestamp = selectedBiobookPageInfo.timestamp;
            }, 100);
          });
        });
        return () => {
          cancelAnimationFrame(frame);
        };
      }
    }
  }, [isComposing, selectedBiobookPageInfo]);
  useEffect(() => {
    if (location.state?.filter) {
      dispatch(setSelectedFilter(location.state.filter));
    } else {
      dispatch(setSelectedConditionIds(location.state?.conditionIds ?? []));
    }
  }, [dispatch, location.state, selectedBiobookPageInfo]);
  useEffect(() => {
    if (!isDemo && !biotwin?.isSample && biotwin?.age != null && !isLoadingGetBiobook && !introDisplayed) {
      setStepsEnabled(true);
    }
  }, [biotwin, introDisplayed, isDemo, isLoadingGetBiobook]);
  useEffect(() => {
    if (isDemo) {
      return pushAssistantContent({
        detail: 'Biobook is a collection of published recommendations specific for a given biotwin.',
        detailDelay: 1000,
        summary: 'This is an individualized Biobook.',
      });
    }
  }, [isDemo]);
  useAppEventListen(
    'help',
    useCallback(() => {
      if (!isDemo && !biotwin?.isSample && biotwin?.age != null) {
        setStepsEnabled(true);
      }
    }, [biotwin, isDemo]),
  );
  return (
    <>
      <div className="flex h-full">
        <BiobookFilterPanel className={'flex-1 min-w-0'} selection />
        <div className={'my-6 border-r border-zinc-400'} />
        <div className={'flex-[3] min-w-0'}>
          {isComposing && <BiobookTableOfContentsSkeleton className="h-full" />}
          {!isComposing && (
            <BiobookTableOfContents
              biobookPages={filteredBiobookPages}
              noEmptyRoots={selectedConditionIds.length > 0}
              onSelectBiobookChapter={(biobookChapter) => {
                if (biobookChapter.phraseCount === 0) {
                  const nodeIndex = tableOfContents.flatNodes.findIndex(
                    (node) => node.data.biobookChapter === biobookChapter,
                  );
                  const node = tableOfContents.flatNodes[nodeIndex];
                  const targetNode = tableOfContents.flatNodes.find(
                    (node, index) => index > nodeIndex && node.data.biobookChapter.phraseCount > 0,
                  );
                  if (targetNode != null) {
                    biobookTableOfContents.current?.expandNode(node);
                    setTimeout(() => {
                      biobookTableOfContents.current?.toggleDetails(targetNode);
                    }, 25);
                    return false;
                  }
                }
              }}
              ref={biobookTableOfContents}
              selectedConditions={selectedConditions}
              selectedFilter={selectedFilter}
              tableOfContents={tableOfContents}
            />
          )}
        </div>
      </div>
      <Steps
        enabled={stepsEnabled}
        initialStep={0}
        steps={steps}
        onExit={() => {
          document.body.classList.remove('introjs-steps-enabled');
          if (stepsEnabled) {
            setStepsEnabled(false);
            setIntroDisplayed(true);
          }
        }}
        onStart={() => document.body.classList.add('introjs-steps-enabled')}
        options={{
          hideNext: false,
          overlayOpacity: 1,
        }}
      />
    </>
  );
  function biobookPagesFactory() {
    let biobookPages =
      selectedFilter === 'favorites'
        ? bookmarkedBiobookPages
        : selectedFilter === 'updates'
          ? updatedBiobookPages
          : selectedFilter === 'multiIndexed'
            ? multiIndexedBiobookPages
            : selectedFilter === 'drugMultiIndexed'
              ? drugMultiIndexedBiobookPages
              : selectedFilter === 'neoplastic'
                ? neoplasticBiobookPages
                : selectedFilter === 'standardAndPolicy'
                  ? standardAndPolicyBiobookPages
                  : biobook;
    if (selectedConditionIds.length > 0) {
      biobookPages = biobookPages.filter((biobookPage) =>
        selectedConditionIds.every((conditionId) => biobookPage.matchedConditionIds.includes(conditionId)),
      );
    }
    return biobookPages.filter((biobookPage) => {
      // eliminates pages that matches with "perioperative setting" and a procedure which are not both in perioperative and not marked as visible
      if (biobookPage.matchedConditionNames.some((conditionName) => isPerioperativeSetting(conditionName))) {
        const matchedRelevantConditions =
          relevantConditions?.filter((rc) => biobookPage.matchedConditionIds.includes(rc.conditionId)) ?? [];
        if (
          matchedRelevantConditions.some(
            (rc) =>
              isProcedure(rc) &&
              !inPerioperativeRelevantConditions.includes(rc) &&
              !allPerioperativeVisibleConditionIds.includes(rc.conditionId),
          )
        ) {
          return false;
        }
      }
      return true;
    });
  }
  function filterBiobookChapters(biobookChapters: IBiobookChapter[], biobookPages: IBiobookPage[]): IBiobookChapter[] {
    return biobookChapters
      .map((biobookChapter) => ({
        ...biobookChapter,
        phraseCount: biobookPages.filter((biobookPage) =>
          biobookPage.bodyLifeMapLabelNames.includes(biobookChapter.labelName),
        ).length,
      }))
      .filter((biobookChapter, _index, array) => {
        return getDescendentBiobookChapters(array, biobookChapter).some((descendent) => descendent.phraseCount > 0);
      });
  }
  function getDescendentBiobookChapters(
    biobookChapters: IBiobookChapter[],
    biobookChapter: IBiobookChapter,
  ): IBiobookChapter[] {
    const children = biobookChapters.filter((child) => child.parentLabelId === biobookChapter.labelId);
    return [biobookChapter, ...children.flatMap((child) => getDescendentBiobookChapters(biobookChapters, child))];
  }
};
const steps: Step[] = [
  {
    element: '[data-name="user-biobook-table-of-contents"]',
    intro: (
      <div className="w-[14rem]">
        There are sections organized according to the body's organ systems and activities of daily living. Please also
        review the pages under the section titles.
      </div>
    ),
    position: 'left',
    title: 'Table Of Contents',
  },
  {
    element: '[data-name="user-biotwin-summary-index"]',
    intro:
      'Please select one or more conditions to review only specific pages in a shortened biobook. After selecting one condition, please note that if no page is specific to double or more combinations, the remaining conditions are disabled.',
    position: 'right',
    title: 'Feature-specific information',
  },
  {
    element: '[data-name="user-biotwin-actions-button"]',
    intro: 'You can select preferences for this biobook.',
    position: 'bottom',
    title: 'Biobook Settings',
  },
];
